
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/contexts/ProfileContext";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export const TestInviteHandler = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [isLoading, setIsLoading] = useState(true);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [testId, setTestId] = useState<string | null>(null);

  // Store the token and test information in sessionStorage when component loads
  useEffect(() => {
    if (token) {
      const returnPath = `/test/invite/${token}`;
      console.log("[TestInviteHandler] Storing test invite token in sessionStorage and localStorage:", token);
      // Session-scoped (for same-tab flows)
      sessionStorage.setItem('pendingTestInviteToken', token);
      sessionStorage.setItem('redirectToJobPage', 'true');
      // Cross-tab persistence (survives OAuth/new tabs)
      try {
        localStorage.setItem('pendingTestInviteToken', token);
        localStorage.setItem('redirectToJobPage', 'true');
        localStorage.setItem('postAuthReturnTo', returnPath);
      } catch (e) {
        console.warn('[TestInviteHandler] Failed to write invite context to localStorage', e);
      }
    }
  }, [token]);

  // Find the test by token - now using the enabled RLS policy
  useEffect(() => {
    const findTestByToken = async () => {
      if (!token) return;
      
      try {
        console.log("[TestInviteHandler] Looking up test with token:", token);
        
        // Find the test using the share_token
        const { data: test, error: testError } = await supabase
          .from('tests')
          .select('id, is_active, invitation_only')
          .eq('share_token', token)
          .single();

        if (testError) {
          console.error("[TestInviteHandler] Test lookup error:", testError);
          throw new Error('Invalid or expired invite link');
        }

        if (!test) {
          console.error("[TestInviteHandler] No test found with token");
          throw new Error('Invalid or expired invite link');
        }

        if (test.is_active === false) {
          console.error("[TestInviteHandler] Test is inactive");
          throw new Error('This test is currently not accepting submissions.');
        }

        // Store invitation_only status for later validation
        if (test.invitation_only) {
          sessionStorage.setItem('testInvitationOnly', 'true');
          try {
            localStorage.setItem('testInvitationOnly', 'true');
          } catch (e) {
            console.warn('[TestInviteHandler] Failed to write testInvitationOnly to localStorage', e);
          }
        }

        console.log("[TestInviteHandler] Found test with ID:", test.id);
        setTestId(test.id);
        
        // Store the test ID in sessionStorage and localStorage
        sessionStorage.setItem('pendingTestId', test.id);
        try {
          localStorage.setItem('pendingTestId', test.id);
        } catch (e) {
          console.warn('[TestInviteHandler] Failed to write pendingTestId to localStorage', e);
        }
        setIsLoading(false);
      } catch (error: any) {
        console.error("[TestInviteHandler] Error finding test:", error);
        setInviteError(error.message);
        setIsLoading(false);
        toast({
          title: "Error",
          description: error.message,
          className: "bg-card text-foreground border-none",
        });
      }
    };

    findTestByToken();
  }, [token, toast]);

  // Handle authenticated users
  useEffect(() => {
    if (!testId) return;
    
    if (user && profile) {
      console.log("[TestInviteHandler] User authenticated, profile loaded. Redirecting to job page");
      
      // Handle employer viewing candidate link
      if (profile.role === 'employer' || profile.role === 'administrator') {
        console.log("[TestInviteHandler] Employer accessed candidate test link");
        
        // Clear pending invite tokens - employers don't need these
        sessionStorage.removeItem('pendingTestInviteToken');
        sessionStorage.removeItem('pendingTestId');
        sessionStorage.removeItem('redirectToJobPage');
        try {
          localStorage.removeItem('pendingTestInviteToken');
          localStorage.removeItem('pendingTestId');
          localStorage.removeItem('redirectToJobPage');
          localStorage.removeItem('postAuthReturnTo');
        } catch {}
        
        toast({
          title: "Candidate Test Link",
          description: "This is a link meant for candidates. Redirecting to test management.",
          className: "bg-card text-foreground border-none",
        });
        navigate('/test-management');
        return;
      }
      
      // For candidates, redirect to job page
      if (profile.role === 'candidate') {
        console.log("[TestInviteHandler] Redirecting candidate to job page");
        
        // Clear the pending invite context since we're successfully redirecting
        sessionStorage.removeItem('pendingTestInviteToken');
        sessionStorage.removeItem('pendingTestId');
        sessionStorage.removeItem('redirectToJobPage');
        try {
          localStorage.removeItem('pendingTestInviteToken');
          localStorage.removeItem('pendingTestId');
          localStorage.removeItem('redirectToJobPage');
          localStorage.removeItem('postAuthReturnTo');
        } catch {}
        
        navigate(`/job/${testId}`);
        return;
      }
    } else if (user && !profile) {
      console.log("[TestInviteHandler] User authenticated but profile not loaded yet");
    } else {
      // If test is found but user is not authenticated, show login/signup options
      setIsLoading(false);
    }
  }, [user, profile, testId, navigate, toast]);

  // Render an auth options UI for unauthenticated users
  if (!isLoading && testId && !user) {
    const returnUrl = `/test/invite/${token}`;

    // Ensure localStorage has post-auth target and invite context
    try {
      localStorage.setItem('postAuthReturnTo', returnUrl);
      if (token) localStorage.setItem('pendingTestInviteToken', token);
      if (testId) localStorage.setItem('pendingTestId', testId);
      localStorage.setItem('redirectToJobPage', 'true');
    } catch {}
    
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-md p-8 space-y-6 bg-card border-border">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold text-foreground">You've Been Invited</h1>
            <p className="text-muted-foreground">
              You've been invited to take an assessment. Please sign in or create an account to continue.
            </p>
          </div>
          
          <div className="flex flex-col gap-4">
            <Button 
              onClick={() => navigate(`/login?returnTo=${encodeURIComponent(returnUrl)}`)}
              className="w-full"
            >
              Sign In
            </Button>
            <Button 
              onClick={() => navigate(`/signup?returnTo=${encodeURIComponent(returnUrl)}`)}
              variant="outline"
              className="w-full"
            >
              Create Account
            </Button>
          </div>
          
          <p className="text-sm text-center text-muted-foreground">
            After signing in or creating an account, you'll be redirected to the assessment.
          </p>
        </Card>
      </div>
    );
  }

  // Loading state or error state
  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      {isLoading && (
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-foreground">Processing your test invitation...</p>
        </div>
      )}
      
      {!isLoading && inviteError && (
        <div className="text-center space-y-4">
          <p className="text-destructive font-medium">{inviteError}</p>
          <p className="text-muted-foreground">Redirecting to homepage...</p>
          <Button onClick={() => navigate('/')} variant="outline">
            Go to Homepage
          </Button>
        </div>
      )}
    </div>
  );
};
